import React from 'react';
import ReactMarkdown from 'react-markdown';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface SessionReplayData {
  session_id: string;
  title: string;
  username: string;
  created_at: string;
  updated_at: string;
  session_data: Record<string, any>;
  messages: Message[];
}

interface SessionReplayModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionData: SessionReplayData | null;
}

const SessionReplayModal: React.FC<SessionReplayModalProps> = ({ isOpen, onClose, sessionData }) => {
  if (!isOpen || !sessionData) return null;

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  const cleanContent = (content: string) => {
    // Remove suggestion markers
    return content.replace(/SUGGESTIONS_START:.*?:SUGGESTIONS_END/g, '').trim();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl" style={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold mb-1">Session Replay - Read Only</h2>
              <div className="text-sm opacity-90 space-y-1">
                <p><strong>Session ID:</strong> {sessionData.session_id}</p>
                <p><strong>Title:</strong> {sessionData.title}</p>
                <p><strong>User:</strong> {sessionData.username}</p>
                <p><strong>Created:</strong> {formatDate(sessionData.created_at)}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Session Metadata */}
        {sessionData.session_data && Object.keys(sessionData.session_data).length > 0 && (
          <div className="bg-gray-50 p-3 border-b text-sm">
            <details className="cursor-pointer">
              <summary className="font-semibold text-gray-700">Session Metadata</summary>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                {sessionData.session_data.region && (
                  <div><strong>Region:</strong> {sessionData.session_data.region}</div>
                )}
                {sessionData.session_data.ec2_pricing_strategy && (
                  <div><strong>Pricing Strategy:</strong> {sessionData.session_data.ec2_pricing_strategy}</div>
                )}
                {sessionData.session_data.storage_type && (
                  <div><strong>Storage Type:</strong> {sessionData.session_data.storage_type}</div>
                )}
                {sessionData.session_data.prefer_amd !== undefined && (
                  <div><strong>Prefer AMD:</strong> {sessionData.session_data.prefer_amd ? 'Yes' : 'No'}</div>
                )}
                {sessionData.session_data.include_graviton !== undefined && (
                  <div><strong>Include Graviton:</strong> {sessionData.session_data.include_graviton ? 'Yes' : 'No'}</div>
                )}
              </div>
            </details>
          </div>
        )}

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '24px', backgroundColor: '#f9fafb' }}>
          {sessionData.messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: '32px 0' }}>No messages in this session</div>
          ) : (
            <>
              {sessionData.messages.map((message, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '16px'
                }}>
                  <div style={{ maxWidth: '75%' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginBottom: '4px',
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                    }}>
                      {message.role === 'assistant' && (
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>🤖 Assistant</span>
                      )}
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {formatDate(message.timestamp)}
                      </span>
                      {message.role === 'user' && (
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#2563eb' }}>👤 You</span>
                      )}
                    </div>
                    <div style={{
                      borderRadius: '8px',
                      padding: '12px',
                      backgroundColor: message.role === 'user' ? '#2563eb' : 'white',
                      color: message.role === 'user' ? 'white' : '#1f2937',
                      border: message.role === 'user' ? 'none' : '1px solid #e5e7eb',
                      boxShadow: message.role === 'user' ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                    }}>
                      <div className="prose prose-sm" style={{ 
                        maxWidth: 'none',
                        color: message.role === 'user' ? 'white' : '#1f2937'
                      }}>
                        <ReactMarkdown>{cleanContent(message.content)}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Spacer to ensure last message is fully visible */}
              <div style={{ height: '120px' }} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-3 rounded-b-lg border-t flex justify-between items-center" style={{ flexShrink: 0 }}>
          <span className="text-sm text-gray-600">
            {sessionData.messages.length} message{sessionData.messages.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionReplayModal;
